import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HighQualityIcon from '@mui/icons-material/HighQuality';
import { Box, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import {DateTime} from "luxon";
import React, { type FC, forwardRef , useEffect, useMemo, useState} from 'react';
import { useSelector } from 'react-redux';

import useHQ from '../../hooks/use-HQ.js';
import { isLayoutEditing } from '../../redux/selectors.js';
import LivestreamHls from '../LivestreamHls.js';
import StreamOffMessage from '../StreamOffMessage.js';



interface PatientStreamContainerProps {
  isPlaying: boolean;
  cameraId: string;
  cameraName: string;
  inactivity: boolean;
  handleFocusStream: (cameraId: string) => void;
  style?: any;
  lastActivity?: string;
  /** When set, the tile renders a top drag-handle strip whenever the
   * matching layout scope is in edit mode. */
  layoutScope?: string;
}
const PatientStreamContainer: FC<PatientStreamContainerProps> = forwardRef(
  ({ isPlaying, cameraId, cameraName, inactivity, handleFocusStream, style, lastActivity, layoutScope }, ref) => {
    const { isHQ, setIsHQ } = useHQ(cameraId);
    const editing = useSelector(
      useMemo(() => isLayoutEditing(layoutScope ?? '__none__'), [layoutScope]),
    );

    const [formattedActivity, setFormattedActivity] = useState<string>();
    useEffect(() => {
      const updateLastActivity = () => {
        if (lastActivity) {
          const dateTime = DateTime.fromISO(lastActivity);
          const timeAgo = dateTime.diffNow(['hours', 'minutes', 'seconds']).toHuman({unitDisplay: 'narrow', maximumFractionDigits: 0}).replace(/-?,?/g, '');
          setFormattedActivity(timeAgo);
        }
        setFormattedActivity(undefined);
      };

      updateLastActivity();
      // We ALSO update LatestLog every minute so the minutesAgo stays up-to-date
      const interval = setInterval(() => {
        updateLastActivity();
      }, 1000); // 5 seconds
      return () => clearInterval(interval);
    }, [lastActivity]);

    return (
      <Box
        ref={ref}
        style={style}
        sx={{ position: 'relative', cursor: 'pointer', height: '100%' }}
        onDoubleClick={() => handleFocusStream(cameraId)}
        onClick={(e) => e.stopPropagation()}
      >
        {editing && layoutScope && (
          <Box
            className="layout-drag-handle"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 9,
              height: 28,
              px: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'move',
              bgcolor: (t) => `${t.palette.primary.main}cc`,
              color: 'primary.contrastText',
              userSelect: 'none',
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>{cameraName}</Typography>
          </Box>
        )}
        <StreamOffMessage isPlaying={isPlaying} inactivity={inactivity} />
        <LivestreamHls cameraId={cameraId} isPlaying={isPlaying && !inactivity} isHQ={isHQ} />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            zIndex: 7,
            p: 1,
            display: 'flex',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            height: '3.4rem',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
            }}
          >
            <Typography
              noWrap
              component={'div'}
              sx={{ color: 'white', margin: 0, overflow: 'initial', textOverflow: 'unset' }}
            >
              {cameraName}
            </Typography>
            <Tooltip
              arrow
              color="primary"
              placement="bottom"
              title={`Change Video quality to ${isHQ ? 'low' : 'high'} quality`}
            >
              <IconButton sx={{ color: isHQ ? '#fac413' : 'white' }} onClick={() => setIsHQ()}>
                <HighQualityIcon />
              </IconButton>
            </Tooltip>
            {formattedActivity && (
              <Typography noWrap sx={{opacity: 0.6, ml: 1, flexShrink: 100}}>
                {formattedActivity}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);

export default PatientStreamContainer;
